/**
 * フォーム統計の公開ページ (/analytics/:token)。
 * 認証不要。集計済みデータのみ表示 (生の回答は含まない)。
 * v3.1.4
 */
import { useParams } from 'react-router-dom';

import { Spinner } from '@/components/ui/spinner';
import { usePublicFormAnalytics } from '@/hooks/useFormAnalytics';

import {
  CHART_TYPE_LABEL,
  ChartRenderer,
} from '@/pages/forms/analytics/ChartRenderer';
import { resolveStyle } from '@/pages/forms/analytics/visualStyles';

export function PublicFormAnalyticsPage() {
  const { token } = useParams<{ token: string }>();
  const { data, isLoading } = usePublicFormAnalytics(token ?? null);

  if (isLoading) return <Spinner />;
  if (!data) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold mb-2">統計を読み込めません</h1>
      </div>
    );
  }
  if (data.status === 'not_found') {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold mb-2">統計が見つかりません</h1>
        <p>URL をご確認ください。</p>
      </div>
    );
  }
  if (data.status === 'expired') {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold mb-2">公開期限が切れています</h1>
        <p>お手数ですが、再発行をご依頼ください。</p>
      </div>
    );
  }

  const a = data.analytics;
  const style = resolveStyle(a.visualStyle);

  return (
    <div
      className="min-h-screen py-10 px-4"
      style={{
        backgroundColor: style.cardBackground,
        color: style.textColor,
        fontFamily: style.fontFamily,
      }}
    >
      <div className="max-w-5xl mx-auto">
        <header className="mb-8">
          <h1
            className="text-2xl font-bold mb-1"
            style={{ color: style.titleColor }}
          >
            {a.formName}
          </h1>
          {a.formDescription && (
            <p className="text-sm mb-2" style={{ color: style.textColor }}>
              {a.formDescription}
            </p>
          )}
          <p className="text-xs" style={{ color: style.textColor, opacity: 0.7 }}>
            回答数: {a.totalSubmissions} 件
          </p>
        </header>

        {a.sections.length === 0 ? (
          <div
            className="rounded-md border p-8 text-center text-sm"
            style={{
              borderColor: style.cardBorder,
              color: style.textColor,
            }}
          >
            統計セクションが登録されていません。
          </div>
        ) : (
          <div className="space-y-6">
            {a.sections.map((s) => (
              <section
                key={s.id}
                className="rounded-md p-5 border"
                style={{
                  backgroundColor: style.cardBackground,
                  borderColor: style.cardBorder,
                }}
              >
                <h2
                  className="text-lg font-semibold mb-1"
                  style={{ color: style.titleColor }}
                >
                  {s.title || CHART_TYPE_LABEL[s.chartType]}
                </h2>
                <p
                  className="text-xs mb-3"
                  style={{ color: style.textColor, opacity: 0.7 }}
                >
                  {s.aggregations.map((x) => x.label).join(' / ')}
                </p>
                <ChartRenderer
                  chartType={s.chartType}
                  aggregations={s.aggregations}
                  style={style}
                  height={320}
                />
              </section>
            ))}
          </div>
        )}

        <footer
          className="mt-10 text-center text-[11px]"
          style={{ color: style.textColor, opacity: 0.5 }}
        >
          Powered by GoEnHub
        </footer>
      </div>
    </div>
  );
}
